// Resource Gantt — 공용 상수
//
// bucket + stack 용량 뷰의 렌더 기본값과 색상 의미를 모아둔다.
// (스펙: saas-web 대상, 성능 1순위 / canvas + d3)

// 렌더 레이아웃 기본값 (px). options로 일부만 덮어쓸 수 있다.
export const GANTT_DEFAULTS = {
  headerHeight: 36, // 상단 시간축 헤더 높이 (스크롤되지 않음)
  rowHeight: 64, // 리소스 1행 높이
  rowPaddingY: 8, // 행 위/아래 여백 (용량 영역과 행 경계 사이)
  laneLeft: 160, // 좌측 리소스 라벨 폭 (AG Grid 트리 연동 전 임시)
  capLineWidth: 2.5, // 버킷별 capacity 라인 두께(점선이 옅어 보이지 않게 굵게)
  bucketStandardCount: 12, // 이 개수가 뷰포트를 꽉 채우도록 버킷 폭을 잡는다(고정 기준). 넘치면 좌우 드래그
  bucketGap: 8, // 버킷 슬롯 내 좌우 여백(막대 사이 간격)
};

// 주문 사양 패널 폭(px) — 좌측 스플리터로 조절하며 뷰 상태에 저장된다.
export const ORDER_PANEL_WIDTH_DEFAULT = 420;
export const ORDER_PANEL_WIDTH_MIN = 300;
export const ORDER_PANEL_WIDTH_MAX = 760;

// 용량 부하율(sum / capacity)에 따른 색상 의미 — 3색
//   정상 <85% green · 빠듯 85–100% amber · 초과 >100% red
export const LOAD_COLORS = {
  normal: "#66bb6a", // green
  tight: "#ffca28", // amber
  over: "#ef5350", // red
};

// 부하 색 임계값(%) — 사용자가 ⚙에서 조정. yellow 이상=노랑, red 이상=빨강, 그 외 green.
export const DEFAULT_LOAD_THRESHOLDS = { yellow: 85, red: 100 };

// 임계값으로 부하율→색 함수를 만든다(팩토리). 기본 loadColor는 기본 임계값 사용.
export const makeLoadColor = (th = DEFAULT_LOAD_THRESHOLDS) => (ratio) => {
  const pct = (ratio || 0) * 100;
  if (pct >= th.red) return LOAD_COLORS.over;
  if (pct >= th.yellow) return LOAD_COLORS.tight;
  return LOAD_COLORS.normal;
};

export const loadColor = makeLoadColor();

// 부하 초과 판정 허용오차(0.1%) — used/total 이 표시 정밀도(반올림) 이하로 미세하게만 클 때
// (부동소수/1ms 잔차 등) 초과로 오판해 빨갛게 뜨는 것을 막는다. 이 안이면 '딱 100%(tight)'로 본다.
export const OVERLOAD_TOLERANCE = 0.001;

// 초과 여부 — capacity 0인데 부하가 있으면 초과, 그 외엔 허용오차를 넘겼을 때만 초과.
export const isOverloaded = (total, cap) => ((cap || 0) > 0 ? total > cap * (1 + OVERLOAD_TOLERANCE) : total > 0);

// 색상용 부하율 — 초과 허용오차 안이면 100% 미만으로 눌러(초과색 대신) tight 로 보이게 한다.
export const loadColorRatio = (total, cap) => {
  if (!((cap || 0) > 0)) return total > 0 ? 1 : 0;
  const r = total / cap;
  return isOverloaded(total, cap) ? r : Math.min(r, 1 - 1e-6);
};

export const CAP_LINE_COLOR = "#90a4ae"; // 버킷 capacity 라인(옅은 블루그레이, 점선)
// 사용 한도(usageLimitRate) 보조 라인 — 초과 판정 기준선. 총능력 cap 라인과 구분되는 색(인디고).
export const LIMIT_LINE_COLOR = "#5c6bc0";
export const OVERLOAD_COLOR = LOAD_COLORS.over; // capacity 초과분(빨강)

// ── AG Grid 테마(ag-theme-balham, App.scss 에서 import) 토큰에 맞춘 색 ──
// 간트는 canvas 라 CSS 변수를 읽을 수 없어 balham light 값을 그대로 옮겨 적는다.
// 테마를 바꾸면 아래 6개만 갈아끼우면 된다.
export const EMPTY_FILL = "#ecf0f1"; // 남은(여유) 용량 빈 영역 — --ag-row-hover-color
export const ROW_DIVIDER_COLOR = "rgba(189, 195, 199, 0.58)"; // 행 구분선 — --ag-row-border-color
export const LABEL_COLOR = "#000000"; // 좌측 리소스명 — --ag-foreground-color (헤더가 아니라 셀 값)
export const LANE_CODE_COLOR = "rgba(0, 0, 0, 0.54)"; // 좌측 리소스 코드 — --ag-secondary-foreground-color
export const GRIDLINE_COLOR = "rgba(189, 195, 199, 0.5)"; // 헤더 세로 가이드라인 — --ag-header-column-separator-color
export const BUCKET_HEADER_BG = "#f5f7f7"; // 상단 시간축 헤더 배경 — --ag-header-background-color
export const LANE_BG = "#fcfdfe"; // 좌측 리소스 컬럼 배경 — --ag-odd-row-background-color
export const HEADER_TEXT_COLOR = "rgba(0, 0, 0, 0.54)"; // 헤더 텍스트 — --ag-header-foreground-color
export const BORDER_COLOR = "#bdc3c7"; // 외곽선·컬럼 경계 — --ag-border-color
export const LANE_SEP_COLOR = BORDER_COLOR; // 레인 ↔ 차트 경계선(같은 토큰)
export const BODY_BG = "#ffffff"; // 차트 본문 배경 — --ag-background-color

// 폰트도 그리드와 맞춘다 — App.scss 의 --app-font-family, balham 의 --ag-font-size(12px),
// 헤더 행 font-weight 600. canvas 는 CSS 변수를 못 읽으니 같은 값을 문자열로 둔다.
export const GANTT_FONT_FAMILY = '"Noto Sans KR", "Noto Sans JP", "Noto Sans SC", sans-serif';
export const HEADER_FONT = `600 12px ${GANTT_FONT_FAMILY}`; // 헤더(연도/날짜/코너)
export const LANE_NAME_FONT = `600 13px ${GANTT_FONT_FAMILY}`; // 좌측 리소스명
export const LANE_CODE_FONT = `12px ${GANTT_FONT_FAMILY}`; // 좌측 리소스 코드
export const SEGMENT_FONT = `11px ${GANTT_FONT_FAMILY}`; // 막대 안 라벨

// 선택된 주문(세그먼트) 강조 — 흰 헤일로 + 진한 라인으로 어떤 색 위에서도 보이게.
// 강조색은 그리드 선택색(--ag-balham-active-color / --ag-selected-row-background-color)과 맞춘다 —
// 표·패널의 선택 표시와 같은 색이라 "이게 선택된 것"이 한눈에 읽힌다.
export const SELECTION_HALO = "#ffffff";
export const SELECTION_OUTLINE = "#102a43";
export const SELECTION_ACCENT = "#0091ea";
export const SELECTION_FILL = "rgba(0, 145, 234, 0.28)";

// 같은 주문 연결선의 진행 방향 화살촉 — 길이(px)와 벌어진 각도(rad).
export const LINK_ARROW_LEN = 7;
export const LINK_ARROW_SPREAD = Math.PI / 7;

// 비가동 버킷(총능력 0) 빗금.
// 부하 0 인 가동 버킷은 EMPTY_FILL 단색으로 채워진다. 그래서 비가동은 배경을 본문색(흰색)으로
// 비우고 빗금 + 테두리로 표시한다 — '단색 회색 = 여유' vs '흰 바탕 + 빗금 = 비가동' 대비.
export const NONWORKING_BG = "#ffffff";
export const NONWORKING_HATCH_COLOR = "rgba(0, 0, 0, 0.3)";
export const NONWORKING_HATCH_WIDTH = 1.25; // 선 두께
export const NONWORKING_HATCH_GAP = 6; // 선 간격(px)
export const SEGMENT_DIM = "#dcdcdc"; // '같은 주문만 강조' 모드에서 나머지 주문을 회색 처리

// 아이템(주문) 색상 팔레트 — Setting(⚙)에서 선택. 같은 아이템=같은 색을 위해 카테고리컬로 순환.
export const ITEM_PALETTES = {
  // 균형 잡힌 기본 — 채도 낮춘 차분한 톤
  classic: ["#6e8cae", "#dca066", "#cf8080", "#8bb0ab", "#88ad81", "#d6c47e", "#a98bb3", "#d3a8ae", "#a08b7c", "#bdb8b3"],
  // Nord — 톤다운된 블루/시안/세이지 + 포인트
  nord: ["#5e81ac", "#81a1c1", "#88c0d0", "#8fbcbb", "#a3be8c", "#ebcb8b", "#d08770", "#bf616a", "#b48ead", "#4c566a"],
  // Muted — 채도 낮춘 무지개(종류 구분 잘 됨)
  muted: ["#6a89a8", "#cf8f6b", "#7fa874", "#c47e7e", "#9486ad", "#97785f", "#c891b3", "#8c8c8c", "#c9b977", "#79a7b0"],
  // Dusty — 옅은 파스텔(더스티)
  dusty: ["#9eb3c2", "#c9ada7", "#b0c4b1", "#d6c1a8", "#c2b9d0", "#a7c4bc", "#d8b4a0", "#b8c9a3", "#cdb7c4", "#9aa7ad"],
};
export const DEFAULT_PALETTE = "classic";

// setup(작업 준비) 구간은 아이템 팔레트와 분리된 전용 색을 사용해 충돌을 막는다.
// 캔버스에서는 라벨 없이 색으로만 구분하므로 '다른 색상(hue)'이 아니라 '명도 대비'로 구분한다.
// → 채도 없는 짙은 차콜. 아이템 팔레트(중간 명도)·부하색(green/amber/red) 위에서 모두 어둡게 떨어져
//   보이고, 새 색상 의미가 생기지 않아 색 코딩과 충돌하지 않는다. 팔레트 톤에만 맞춰 미세 조정.
export const PALETTE_SETUP_COLORS = {
  classic: "#263238",
  nord: "#1f2430",
  muted: "#2f3437",
  dusty: "#4a5560",
};

export const getPaletteSetupColor = (palette = DEFAULT_PALETTE) => {
  return PALETTE_SETUP_COLORS[palette] || PALETTE_SETUP_COLORS[DEFAULT_PALETTE];
};
